import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Props } from "../../core/types/tsx/components/order-detail/ProviderCard.types";

function getInitials(name?: string): string {
	const clean = (name ?? "").trim();
	if (!clean) return "--";
	const parts = clean.split(/\s+/).filter(Boolean);
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
}

export function ProviderCard({ provider, showProfileLink = true }: Props) {
	const navigate = useNavigate();
	const [avatarLoadError, setAvatarLoadError] = useState(false);
	const resolvedInitials = useMemo(() => getInitials(provider.name), [provider.name]);
	const avatarUrl = provider.avatarUrl?.trim() || "";

	useEffect(() => {
		setAvatarLoadError(false);
	}, [avatarUrl]);

	const hasPhone = provider.phone.trim().length > 0;
	const hasEmail = provider.email.trim().length > 0;
	const hasReviewChatAction = typeof provider.onReviewChat === "function";
	const shouldShowContactInfo = hasPhone || hasEmail;
	const showImageAvatar = avatarUrl.length > 0 && !avatarLoadError;
	return (
		<section className='rounded-2xl bg-white p-6 soft-border'>
			{/* Header */}
			<div className='flex items-center justify-between mb-4'>
				<p className='text-sm font-semibold text-gray-700'>
					Proveedor del Servicio
				</p>
				{showProfileLink ? (
					<button
						type='button'
						onClick={() => navigate(`/proveedor/${provider.id}`)}
						className='cursor-pointer text-sm font-medium text-primary hover:underline'
					>
						Ver perfil del proveedor →
					</button>
				) : null}
			</div>

			{/* Provider info */}
			<div className='flex items-center gap-4'>
				{showImageAvatar ? (
					<div className='h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-gray-100'>
						<img
							src={avatarUrl}
							alt={provider.name}
							className='h-full w-full object-cover'
							onError={() => setAvatarLoadError(true)}
						/>
					</div>
				) : (
					<div className='flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-base font-semibold text-primary'>
						{resolvedInitials}
					</div>
				)}
				<div>
					<div className='flex items-center gap-1.5 text-sm font-semibold text-gray-800'>
						{provider.name}
						{provider.verified && (
							<svg
								xmlns='http://www.w3.org/2000/svg'
								className='h-4.5 w-4.5 text-primary'
								viewBox='0 0 20 20'
								fill='currentColor'
							>
								<path
									fillRule='evenodd'
									d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
									clipRule='evenodd'
								/>
							</svg>
						)}
					</div>
					{/*
					<div className='flex items-center gap-1 text-xs text-gray-500 mt-0.5'>
						<span className='text-primary'>★</span>
						<span className='font-semibold text-gray-700'>
							{provider.rating.toFixed(1)}
						</span>
						<span className='text-gray-400'>
							({provider.reviews.toLocaleString()} reseñas)
						</span>
					</div>*/}
					{/* reseñas */}
				</div>
			</div>

			{/* Divider */}
			<div className='my-4 h-px bg-gray-100' />

			{/* Contact info */}
			{shouldShowContactInfo ? (
				<div className='grid gap-3 sm:grid-cols-2'>
					{hasPhone ? (
						<a
							href={`tel:${provider.phone}`}
							className='flex cursor-pointer items-center gap-3 rounded-xl px-1 py-2 text-sm'
						>
							<span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-primary'>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									className='h-4 w-4'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'
								>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M3 5a2 2 0 012-2h2.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-1.518.76a11.042 11.042 0 006.105 6.105l.76-1.518a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.163 21 3 14.837 3 7V5z'
									/>
								</svg>
							</span>
							<div>
								<p className='text-xs text-gray-400'>Teléfono</p>
								<p className='font-medium text-primary'>
									{provider.phone}
								</p>
							</div>
						</a>
					) : null}
					{hasEmail ? (
						<a
							href={`mailto:${provider.email}`}
							className='flex cursor-pointer items-center gap-3 rounded-xl px-1 py-2 text-sm'
						>
							<span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-primary'>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									className='h-4 w-4'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'
								>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-2 9H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2z'
									/>
								</svg>
							</span>
							<div>
								<p className='text-xs text-gray-400'>Email</p>
								<p className='font-medium text-primary'>
									{provider.email}
								</p>
							</div>
						</a>
					) : null}
				</div>
			) : null}

			{/* Chat button */}
			{hasReviewChatAction ? (
				<button
					type='button'
					onClick={provider.onReviewChat}
					className='cursor-pointer mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-primary-hover'
				>
					<svg
						xmlns='http://www.w3.org/2000/svg'
						className='h-5 w-5'
						fill='none'
						viewBox='0 0 24 24'
						stroke='currentColor'
					>
						<path
							strokeLinecap='round'
							strokeLinejoin='round'
							strokeWidth={2}
							d='M8 10h.01M12 10h.01M16 10h.01M9 16h6m2 5H7a2 2 0 01-2-2V7a2 2 0 012-2h3l2-2h3a2 2 0 012 2v14a2 2 0 01-2 2z'
						/>
					</svg>
					Revisar chat
				</button>
			) : null}
		</section>
	);
}

export default ProviderCard;
